import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getHatateToken } from "helpers/utils";

interface TradingFormProps {
  onSubmit: (orderData: OrderData) => void;
  symbol: string;
}

interface OrderData {
  symbol: string;
  action: 'BUY' | 'SELL';
  price: number;
  amount: number;
  stopLoss?: number;
  takeProfit?: number;
}

// Usar directamente el endpoint de IA
const LLM_ENDPOINT = 'https://backend.anubisai.net/api/bot/llm';

// Flag para usar datos mock durante desarrollo
const USE_MOCK_DATA = process.env.NODE_ENV === 'development' || true;

const TradingForm: React.FC<TradingFormProps> = ({ onSubmit, symbol }) => {
  const [orderData, setOrderData] = useState<OrderData>({
    symbol: symbol,
    action: 'BUY',
    price: 0,
    amount: 0.1,
    stopLoss: undefined,
    takeProfit: undefined
  });
  
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [useLimitOrder, setUseLimitOrder] = useState<boolean>(false);
  const [useStopLoss, setUseStopLoss] = useState<boolean>(false);
  const [useTakeProfit, setUseTakeProfit] = useState<boolean>(false);
  
  // Actualizar el símbolo cuando cambia en las props
  useEffect(() => {
    setOrderData(prev => ({ ...prev, symbol }));
    fetchCurrentPrice();
  }, [symbol]);
  
  // Obtener el precio actual del mercado
  const fetchCurrentPrice = async () => {
    try {
      setIsLoading(true);
      
      if (USE_MOCK_DATA) {
        // Generar precio simulado
        const mockPrice = generateMockPrice(symbol);
        setCurrentPrice(mockPrice);
        
        // Actualizar el precio en el formulario si no es una orden límite
        if (!useLimitOrder) {
          setOrderData(prev => ({ ...prev, price: mockPrice }));
        }
        
        // Calcular stop loss y take profit predeterminados
        updateDefaultStopLossAndTakeProfit(mockPrice);
        
        setIsLoading(false);
        return;
      }
      
      // En producción, consultar al LLM
      const apiToken = getHatateToken();
      const timestamp = new Date().toISOString();
      
      const response = await fetch(LLM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${apiToken}`
        },
        body: JSON.stringify({
          prompt: [{
            content: `¿Cuál es el precio actual de ${symbol}? Por favor responde solo con el valor numérico.`,
            role: 'user',
            time: timestamp
          }],
          chatId: `trading_price_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          skill: 'ambipar_v1',
          module: 'ambipar_hazmat_tech',
          contextData: { symbol, dataType: 'currentPrice' }
        })
      });
      
      if (!response.ok) {
        throw new Error(`Error en la solicitud: ${response.status}`);
      }
      
      const data = await response.json();
      let price;
      
      // Extraer el precio de la respuesta
      if (data.response || data.text || data.content) {
        const responseText = data.response || data.text || data.content;
        const priceMatch = responseText.match(/\$?(\d+[.,]?\d*)/);
        if (priceMatch) {
          price = parseFloat(priceMatch[1].replace(',', '.'));
        } else {
          // Si no podemos extraer un precio, usar un valor simulado
          price = generateMockPrice(symbol);
        }
      } else if (data.price) {
        price = data.price;
      } else {
        price = generateMockPrice(symbol);
      }
      
      setCurrentPrice(price);
      
      // Actualizar el precio en el formulario si no es una orden límite
      if (!useLimitOrder) {
        setOrderData(prev => ({ ...prev, price }));
      }
      
      // Calcular stop loss y take profit predeterminados
      updateDefaultStopLossAndTakeProfit(price);
      
    } catch (error) {
      console.error('Error al obtener el precio actual:', error);
      
      // En caso de error, usar precio simulado
      const mockPrice = generateMockPrice(symbol);
      setCurrentPrice(mockPrice);
      
      if (!useLimitOrder) {
        setOrderData(prev => ({ ...prev, price: mockPrice }));
      }
      
      updateDefaultStopLossAndTakeProfit(mockPrice);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Actualizar stop loss y take profit basados en el precio actual
  const updateDefaultStopLossAndTakeProfit = (price: number) => {
    const defaultStopLoss = orderData.action === 'BUY' 
      ? price * 0.95 // 5% por debajo para compras
      : price * 1.05; // 5% por encima para ventas
      
    const defaultTakeProfit = orderData.action === 'BUY'
      ? price * 1.1 // 10% por encima para compras
      : price * 0.9; // 10% por debajo para ventas
      
    if (!useStopLoss && !useTakeProfit) {
      setOrderData(prev => ({
        ...prev,
        stopLoss: parseFloat(defaultStopLoss.toFixed(2)),
        takeProfit: parseFloat(defaultTakeProfit.toFixed(2))
      }));
    }
  };
  
  // Generar precio simulado según el símbolo
  const generateMockPrice = (symbol: string): number => {
    switch (symbol) {
      case 'BTC/USD':
        return 55000 + Math.random() * 5000;
      case 'ETH/USD':
        return 3000 + Math.random() * 300;
      case 'XRP/USD':
        return 0.5 + Math.random() * 0.1;
      case 'SOL/USD':
        return 100 + Math.random() * 20;
      default:
        return 100 + Math.random() * 10;
    }
  };
  
  // Manejar cambios en los inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'action') {
      const action = value as 'BUY' | 'SELL';
      setOrderData(prev => {
        // Recalcular stop loss y take profit cuando se cambia la acción
        let stopLoss = prev.stopLoss;
        let takeProfit = prev.takeProfit;
        
        if (currentPrice && (useStopLoss || useTakeProfit)) {
          stopLoss = action === 'BUY'
            ? currentPrice * 0.95
            : currentPrice * 1.05;
            
          takeProfit = action === 'BUY'
            ? currentPrice * 1.1
            : currentPrice * 0.9;
        }
        
        return {
          ...prev,
          action,
          stopLoss: parseFloat(stopLoss?.toFixed(2) || '0'),
          takeProfit: parseFloat(takeProfit?.toFixed(2) || '0')
        };
      });
    } else if (name === 'price' || name === 'amount' || name === 'stopLoss' || name === 'takeProfit') {
      setOrderData(prev => ({
        ...prev,
        [name]: parseFloat(value)
      }));
    } else {
      setOrderData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Manejar envío del formulario
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Preparar los datos finales de la orden
    const finalOrderData = {
      ...orderData,
      // Si no se utilizan stop loss o take profit, eliminarlos del objeto
      ...(useStopLoss ? {} : { stopLoss: undefined }),
      ...(useTakeProfit ? {} : { takeProfit: undefined })
    };
    
    onSubmit(finalOrderData);
    
    // Resetear campos específicos después del envío
    if (!useLimitOrder && currentPrice) {
      setOrderData(prev => ({
        ...prev,
        price: currentPrice,
        amount: 0.1 // Resetear a un valor predeterminado
      }));
    }
  };
  
  // Manejar cambio en los toggles
  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    
    if (name === 'useLimitOrder') {
      setUseLimitOrder(checked);
      
      // Si se desactiva la orden límite, actualizar al precio actual
      if (!checked && currentPrice) {
        setOrderData(prev => ({ ...prev, price: currentPrice }));
      }
    } else if (name === 'useStopLoss') {
      setUseStopLoss(checked);
    } else if (name === 'useTakeProfit') {
      setUseTakeProfit(checked);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">Símbolo</label>
        <input 
          type="text" 
          className="form-control" 
          value={symbol} 
          readOnly 
        />
      </div>
      
      <div className="mb-3">
        <label className="form-label">Acción</label>
        <select 
          className="form-select"
          name="action"
          value={orderData.action}
          onChange={handleChange}
        >
          <option value="BUY">Comprar</option>
          <option value="SELL">Vender</option>
        </select>
      </div>
      
      <div className="mb-3">
        <div className="form-check form-switch">
          <input 
            className="form-check-input" 
            type="checkbox" 
            id="useLimitOrder" 
            name="useLimitOrder"
            checked={useLimitOrder}
            onChange={handleToggleChange}
          />
          <label className="form-check-label" htmlFor="useLimitOrder">
            Usar precio límite
          </label>
        </div>
      </div>
      
      <div className="mb-3">
        <label className="form-label">
          Precio {!useLimitOrder && currentPrice && `(Actual: $${currentPrice.toFixed(2)})`}
        </label>
        <input 
          type="number" 
          className="form-control" 
          name="price"
          value={orderData.price}
          onChange={handleChange}
          readOnly={!useLimitOrder}
          required
          step="0.01"
        />
      </div>
      
      <div className="mb-3">
        <label className="form-label">Cantidad</label>
        <input 
          type="number" 
          className="form-control" 
          name="amount"
          value={orderData.amount}
          onChange={handleChange}
          required
          step="0.01"
          min="0.01"
        />
      </div>
      
      <div className="mb-3">
        <div className="form-check form-switch">
          <input 
            className="form-check-input" 
            type="checkbox" 
            id="useStopLoss" 
            name="useStopLoss"
            checked={useStopLoss}
            onChange={handleToggleChange}
          />
          <label className="form-check-label" htmlFor="useStopLoss">
            Usar Stop Loss
          </label>
        </div>
      </div>
      
      {useStopLoss && (
        <div className="mb-3">
          <label className="form-label">Stop Loss</label>
          <input 
            type="number" 
            className="form-control" 
            name="stopLoss"
            value={orderData.stopLoss || ''}
            onChange={handleChange}
            required={useStopLoss}
            step="0.01"
          />
        </div>
      )}
      
      <div className="mb-3">
        <div className="form-check form-switch">
          <input 
            className="form-check-input" 
            type="checkbox" 
            id="useTakeProfit" 
            name="useTakeProfit"
            checked={useTakeProfit}
            onChange={handleToggleChange}
          />
          <label className="form-check-label" htmlFor="useTakeProfit">
            Usar Take Profit
          </label>
        </div>
      </div>
      
      {useTakeProfit && (
        <div className="mb-3">
          <label className="form-label">Take Profit</label>
          <input 
            type="number" 
            className="form-control" 
            name="takeProfit"
            value={orderData.takeProfit || ''}
            onChange={handleChange}
            required={useTakeProfit}
            step="0.01"
          />
        </div>
      )}
      
      <div className="mb-3 d-flex justify-content-between">
        <button 
          type="button" 
          className="btn btn-secondary"
          onClick={fetchCurrentPrice}
          disabled={isLoading}
        >
          {isLoading ? 'Actualizando...' : 'Actualizar Precio'}
        </button>
        
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isLoading}
        >
          {orderData.action === 'BUY' ? 'Comprar' : 'Vender'}
        </button>
      </div>
      
      {currentPrice && (
        <div className="text-center mt-3">
          <small className="text-muted">
            Costo estimado: ${(orderData.price * orderData.amount).toFixed(2)}
          </small>
        </div>
      )}
    </form>
  );
};

export default TradingForm;